//@ts-ignore
import { tw } from "@careinfo/tailwind";
import { useQueryClient } from "@tanstack/react-query";
import {
  File,
  FileArchive,
  FileAudio,
  FileCheck,
  FileImage,
  FileText,
  FileVideo,
  FileX,
  PlusIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import CommentIcon from "../assets/CommentIcon";
import CrossIcon from "../assets/CrossIcon";
import APIS from "../constant/Endpoint";
import useGetHook from "../customHooks/useGetHook";
import usePostHook from "../customHooks/usePostHook";
import useUploadFileHook from "../customHooks/useUploadFileHook";
import RecursiveComment from "./RecursiveComment";
import Comment from "./OrderDetailPage/Comment";
import { deleteApiData } from "../helpers/AxiosInstance";
import { toast } from "react-toastify";

const Remark = ({
  selectedOrder,
  receiver,
  canInput = true,
}: {
  selectedOrder?: string;
  receiver?: string;
  canInput: boolean;
}) => {
  const [comment, setComment] = useState<any>();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { data: profile, refetch } = useGetHook({
    queryKey: [`profile`],
    url: `${APIS.USER}me/`,
    params: {},
  });
  const { data: remarks } = useGetHook({
    queryKey: [`remakrs-${selectedOrder}-${receiver}`],
    url: APIS.COMMENT,
    params: {
      limit: 200,
      offset: 0,
      object_id: selectedOrder,
    },
  });

  const { mutateAsync, isPostLoading } = usePostHook({
    queryKey: [`remakrs-${selectedOrder}-${receiver}`],
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<any>({
    // resolver: yupResolver(orderRemarksValidationForm),
  });

  const handleCreateReview = async (data: any, id: any) => {
    const url = `${APIS.COMMENT}`;
    const isFile =
      typeof data?.comment === "string" && data?.comment.startsWith("media");

    let formData = {
      ...data,
      type: isFile ? "file" : "text",
      branch: profile?.default_branch?.id,
      parent: id,
      object_id: selectedOrder,
    };
    try {
      await mutateAsync({ url, formData, setError });
      reset();
      setComment(null);
      setCommentText("");
      setPreviewUrl(null);
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [commentText]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();

      if (commentText.trim()) {
        handleSubmit(() =>
          handleCreateReview({ comment: commentText }, comment?.id)
        )();
        setCommentText("");
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value);
  };

  const { uploadFile, progress } = useUploadFileHook({
    setIsUploading,
    queryKey: ["file"],
  });

  const generateFileId = () => {
    return (
      "file_" +
      Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("Only images, PDF, Word, and Excel files are allowed.");
      return;
    }

    setSelectedFile(file);
    const fileURL = URL.createObjectURL(file);
    setPreviewUrl(fileURL);
    handleUpload(file);
  };

  const handleUpload = async (file: File) => {
    const fileId = generateFileId();
    const res = await uploadFile(file, fileId, selectedOrder);
    setCommentText(res);
  };

  useEffect(() => {
    return () => {
      // Clean up the preview URL on unmount or file change
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const getFileIconComponent = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();

    switch (ext) {
      case "pdf":
      case "doc":
      case "docx":
      case "txt":
        return <FileText className={tw`w-8 h-8 text-blue-600`} />;
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return <FileImage className={tw`w-8 h-8 text-green-600`} />;
      case "mp3":
      case "wav":
        return <FileAudio className={tw`w-8 h-8 text-purple-600`} />;
      case "mp4":
      case "avi":
        return <FileVideo className={tw`w-8 h-8 text-pink-600`} />;
      case "zip":
      case "rar":
      case "7z":
        return <FileArchive className={tw`w-8 h-8 text-yellow-600`} />;
      case "json":
        return <FileCheck className={tw`w-8 h-8 text-teal-600`} />;
      case "exe":
        return <FileX className={tw`w-8 h-8 text-red-600`} />;
      default:
        return <File className={tw`w-8 h-8 text-gray-500`} />;
    }
  };
  const handleDeleteComment = async (id: string) => {
    const url = `${APIS.COMMENT}${id}/`;
    try {
      const res = await deleteApiData({ url, setError: () => {} });
      if (res?.status === 204) {
        toast.success(res?.message);
        refetch();
      }
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <>
      <section className={tw`bg-white  mt-4`}>
        <div className={tw`mx-auto px-4`}>
          <h2 className={tw`text-center uppercase`}>{receiver}</h2>
          {canInput && (
            <div className={tw`mt-4 `}>
              <form
                className={tw`px-4 py-2 w-full border-t border-gray-300 bg-white`}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (commentText.trim()) {
                    handleCreateReview({ comment: commentText }, comment?.id);
                    setCommentText("");
                  }
                }}
              >
                {comment && (
                  <div className={tw`mb-2 border-t pt-2`}>
                    <div className={tw`flex justify-between items-center`}>
                      <p className={tw`text-sm text-gray-700`}>
                        Replying to{" "}
                        <span className={tw`font-medium`}>
                          {comment?.user?.name}
                        </span>
                      </p>
                      <div
                        className={tw`cursor-pointer text-gray-500`}
                        onClick={() => setComment("")}
                      >
                        <CrossIcon />
                      </div>
                    </div>
                    <p className={tw`text-gray-500 text-xs ml-2`}>
                      <Comment comment={comment.comment} />
                    </p>
                  </div>
                )}
                <div className={tw`flex items-center gap-2`}>
                  <label
                    title="Upload Files"
                    className={tw`cursor-pointer hover:bg-gray-200 rounded-full text-gray-600 `}
                  >
                    <PlusIcon className={tw`w-5 h-5`} />
                    <input
                      type="file"
                      hidden
                      onChange={handleFileChange}
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*"
                    />
                  </label>
                  {previewUrl ? (
                    <div className={tw`mb-2 w-full border rounded-lg relative`}>
                      {selectedFile?.type.startsWith("image/") ? (
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className={tw`w-20 h-20 object-fit rounded-lg border border-gray-300`}
                        />
                      ) : (
                        <div
                          className={tw`text-sm flex items-center gap-x-2 text-gray-600 bg-gray-100 p-2 rounded-md`}
                        >
                          {getFileIconComponent(selectedFile?.name)}
                          {selectedFile?.name} (
                          {Math.round(selectedFile?.size / 1024)} KB)
                        </div>
                      )}
                      <button
                        type="button"
                        disabled={!isUploading}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCommentText("");
                          setSelectedFile(null);
                          setPreviewUrl(null);
                        }}
                        className={tw`absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full w-5 h-5 flex items-center justify-center text-xs text-gray-600 hover:bg-gray-300  transition`}
                        aria-label="Remove file"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <textarea
                      ref={textareaRef}
                      placeholder="Type a comment..."
                      value={commentText}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      rows={1}
                      className={tw`flex-1 text-sm border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary text-gray-700 overflow-hidden resize-none`}
                    />
                  )}
                  <button
                    type="submit"
                    disabled={isPostLoading || isUploading}
                    className={tw`text-primary hover:text-black focus:outline-none`}
                  >
                    <CommentIcon />
                  </button>
                </div>
              </form>
            </div>
          )}
           <div className={tw`max-h-[50svh] overflow-y-auto`}>
            {remarks?.results?.map((item, index) => (
              <RecursiveComment
                key={item?.id}
                item={item}
                setComment={setComment}
                handleDelete={handleDeleteComment}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Remark;
